import type { Messages } from '../i18n';
import { renderMarkdown } from '../markdown';
import { clampSize, loadDialogSize, saveDialogSize, VIEWPORT_MARGIN } from './dialog-size';

// Mirrors the server's zod default e-mail grammar (stricter than HTML5
// checkValidity) so a value that passes here never 400s at ingest — and the
// charset excludes ?&= on purpose (mailto-link safety server-side).
export const EMAIL_PATTERN = /^[A-Za-z0-9_'+.-]+@([A-Za-z0-9-]+\.)+[A-Za-z]{2,}$/;

export interface Dialog {
  open(anchor: DOMRect, selectorPreview: string, prefillEmail?: string): void;
  close(): void;
  /** What Escape means here: exit fullscreen if in it, otherwise cancel. */
  escape(): void;
  setBusy(busy: boolean): void;
  showSuccess(): void;
  showError(): void;
  destroy(): void;
}

/**
 * Accessing the `localStorage` global itself (not just its methods) throws
 * `SecurityError` in cookies-blocked browsers. Called fresh at each use
 * (never cached at module load) since the security setting can differ per
 * document lifetime — matches gate.ts's per-call guarding convention.
 */
function safeLocalStorage(): Storage | null {
  try {
    return window.localStorage;
  } catch {
    return null;
  }
}

export function createDialog(opts: {
  root: ShadowRoot;
  messages: Messages;
  showEmailField: boolean;
  onSubmit(comment: string, reporterEmail: string | null): void;
  onCancel(): void;
}): Dialog {
  const { messages } = opts;

  // A modal <dialog> rather than a plain positioned box, because that is what
  // keeps the widget usable while the *host page* has its own modal open.
  // Modal libraries (Radix/shadcn, Headless UI, MUI …) trap focus with
  // document-level focusin/focusout handlers and pull focus straight back
  // whenever it moves to something outside their container — and a focus
  // change crossing our shadow boundary is retargeted to the widget host,
  // which is by definition outside. The textarea would lose focus before the
  // first keystroke. While this dialog is shown modally the rest of the
  // document is inert, so those refocus calls are no-ops. The top layer also
  // puts it above host modals of its own (native <dialog>s included),
  // whatever the configured z-index.
  const container = document.createElement('dialog');
  container.className = 'bf-dialog';
  // Native <dialog> already implies role/modality; the attribute is kept for
  // the pre-2022 fallback below, where it is an unknown element.
  container.setAttribute('role', 'dialog');
  container.setAttribute('aria-label', messages.commentLabel);

  const selectorPreview = document.createElement('div');
  selectorPreview.className = 'bf-selector';
  selectorPreview.title = messages.selectedElement;

  const commentLabel = document.createElement('label');
  commentLabel.textContent = messages.commentLabel;
  commentLabel.htmlFor = 'bf-comment';

  const textarea = document.createElement('textarea');
  textarea.id = 'bf-comment';
  textarea.placeholder = messages.commentPlaceholder;
  textarea.maxLength = 4000;

  // Basic Markdown formatting: comment stays plain text on the wire; the
  // admin UI renders it. setRangeText keeps the browser's undo stack intact.
  function wrapSelection(prefix: string, suffix: string): void {
    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const selected = textarea.value.slice(start, end);
    textarea.setRangeText(prefix + selected + suffix, start, end, 'end');
    const cursor = start === end ? start + prefix.length : end + prefix.length + suffix.length;
    textarea.setSelectionRange(cursor, cursor);
    textarea.focus();
  }

  function toggleListPrefix(): void {
    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const lineStart = textarea.value.lastIndexOf('\n', start - 1) + 1;
    const segment = textarea.value.slice(lineStart, end);
    const lines = segment.split('\n');
    const allListed = lines.every((line) => line.startsWith('- ') || line.trim() === '');
    const replaced = lines
      .map((line) => {
        if (line.trim() === '') return line;
        return allListed ? line.slice(2) : `- ${line}`;
      })
      .join('\n');
    textarea.setRangeText(replaced, lineStart, end, 'end');
    textarea.focus();
  }

  const toolbar = document.createElement('div');
  toolbar.className = 'bf-toolbar';

  const formatActions: ReadonlyArray<[string, string, string, () => void]> = [
    ['B', 'bf-fmt-bold', messages.formatBold, () => wrapSelection('**', '**')],
    ['I', 'bf-fmt-italic', messages.formatItalic, () => wrapSelection('*', '*')],
    ['</>', 'bf-fmt-code', messages.formatCode, () => wrapSelection('`', '`')],
    ['• –', 'bf-fmt-list', messages.formatList, toggleListPrefix],
  ];
  for (const [text, className, title, action] of formatActions) {
    const button = document.createElement('button');
    button.type = 'button';
    button.className = className;
    button.textContent = text;
    button.title = title;
    button.setAttribute('aria-label', title);
    button.addEventListener('click', action);
    toolbar.appendChild(button);
  }

  const toolbarSpacer = document.createElement('div');
  toolbarSpacer.className = 'bf-toolbar-spacer';

  const previewButton = document.createElement('button');
  previewButton.type = 'button';
  previewButton.className = 'bf-btn-preview';
  previewButton.textContent = messages.preview;
  previewButton.title = messages.preview;
  previewButton.setAttribute('aria-pressed', 'false');

  const fullscreenButton = document.createElement('button');
  fullscreenButton.type = 'button';
  fullscreenButton.className = 'bf-btn-full';
  fullscreenButton.textContent = '⛶';
  fullscreenButton.title = messages.fullscreen;
  fullscreenButton.setAttribute('aria-label', messages.fullscreen);
  fullscreenButton.setAttribute('aria-pressed', 'false');

  toolbar.append(toolbarSpacer, previewButton, fullscreenButton);

  const actions = document.createElement('div');
  actions.className = 'bf-actions';

  const cancelButton = document.createElement('button');
  cancelButton.type = 'button';
  cancelButton.className = 'bf-btn bf-btn-secondary';
  cancelButton.textContent = messages.cancel;

  const submitButton = document.createElement('button');
  submitButton.type = 'button';
  submitButton.className = 'bf-btn bf-btn-primary';
  submitButton.textContent = messages.submit;

  const status = document.createElement('div');
  status.className = 'bf-status';
  status.style.display = 'none';
  status.setAttribute('role', 'status');

  const preview = document.createElement('div');
  preview.className = 'bf-preview';
  preview.style.display = 'none';
  // A stray click on a rendered link must not navigate the host page and
  // lose the comment.
  preview.addEventListener('click', (event) => {
    if (event.target instanceof Element && event.target.closest('a')) {
      event.preventDefault();
    }
  });

  let emailInput: HTMLInputElement | null = null;
  if (opts.showEmailField) {
    emailInput = document.createElement('input');
    emailInput.type = 'email';
    emailInput.className = 'bf-email';
    emailInput.placeholder = messages.emailLabel;
    emailInput.setAttribute('aria-label', messages.emailLabel);
    emailInput.maxLength = 320;
    emailInput.autocomplete = 'email';
  }

  actions.append(cancelButton, submitButton);
  container.append(
    selectorPreview,
    commentLabel,
    toolbar,
    textarea,
    preview,
    ...(emailInput ? [emailInput] : []),
    actions,
    status,
  );
  opts.root.appendChild(container);

  let previousFocus: Element | null = null;
  let previewing = false;
  let fullscreen = false;
  let lastAnchor: DOMRect | null = null;
  // Size right after `position()` runs on open — compared against the size at
  // close so a bare open/close (no grip drag) never persists a viewport-
  // clamped default as if the user had chosen it.
  let openedSize: { width: number; height: number } | null = null;

  function setPreviewing(next: boolean): void {
    previewing = next;
    if (previewing) preview.innerHTML = renderMarkdown(textarea.value);
    textarea.style.display = previewing ? 'none' : '';
    preview.style.display = previewing ? '' : 'none';
    previewButton.setAttribute('aria-pressed', String(previewing));
    if (!previewing) textarea.focus();
  }

  function setFullscreen(next: boolean): void {
    fullscreen = next;
    container.classList.toggle('bf-dialog-full', fullscreen);
    fullscreenButton.setAttribute('aria-pressed', String(fullscreen));
    fullscreenButton.title = fullscreen ? messages.exitFullscreen : messages.fullscreen;
    fullscreenButton.setAttribute(
      'aria-label',
      fullscreen ? messages.exitFullscreen : messages.fullscreen,
    );
    if (!fullscreen && lastAnchor) position(lastAnchor);
  }

  previewButton.addEventListener('click', () => setPreviewing(!previewing));
  fullscreenButton.addEventListener('click', () => setFullscreen(!fullscreen));

  function submit(): void {
    const comment = textarea.value.trim();
    if (!comment) {
      textarea.focus();
      return;
    }
    if (
      emailInput &&
      emailInput.value !== '' &&
      (!emailInput.checkValidity() || !EMAIL_PATTERN.test(emailInput.value.trim()))
    ) {
      emailInput.focus();
      return;
    }
    opts.onSubmit(comment, emailInput && emailInput.value !== '' ? emailInput.value.trim() : null);
  }

  function handleEscape(): void {
    if (fullscreen) {
      setFullscreen(false);
    } else {
      opts.onCancel();
    }
  }

  // Escape on a modal <dialog> also reaches the UA as a close request. Left
  // alone it would close the element behind the widget's back — with the
  // controller still in `composing`, and skipping the fullscreen-exits-first
  // step. Take it over and route it through the same handler as the keydown.
  container.addEventListener('cancel', (event) => {
    event.preventDefault();
    handleEscape();
  });

  cancelButton.addEventListener('click', () => opts.onCancel());
  submitButton.addEventListener('click', submit);
  container.addEventListener('keydown', (event) => {
    if (event.key === 'Escape') {
      event.stopPropagation();
      handleEscape();
    } else if (event.key === 'Enter' && (event.metaKey || event.ctrlKey)) {
      event.preventDefault();
      submit();
    } else if (!previewing && (event.metaKey || event.ctrlKey) && event.key.toLowerCase() === 'b') {
      event.preventDefault();
      wrapSelection('**', '**');
    } else if (!previewing && (event.metaKey || event.ctrlKey) && event.key.toLowerCase() === 'i') {
      event.preventDefault();
      wrapSelection('*', '*');
    }
  });

  function setStatus(kind: 'sending' | 'success' | 'error' | null): void {
    status.className = 'bf-status';
    status.textContent = '';
    if (kind === null) {
      status.style.display = 'none';
      return;
    }
    status.style.display = 'flex';
    if (kind === 'sending') {
      const spinner = document.createElement('span');
      spinner.className = 'bf-spinner';
      status.append(spinner, document.createTextNode(messages.sending));
    } else if (kind === 'success') {
      status.classList.add('bf-status-success');
      status.textContent = `✓ ${messages.success}`;
    } else {
      status.classList.add('bf-status-error');
      status.textContent = messages.error;
    }
  }

  /**
   * Safari 15.0–15.3 has shadow DOM but no `<dialog>`: opening degrades to a
   * plain panel there (no top layer, no inert page) rather than not opening
   * at all. `.bf-dialog[open]` drives visibility in both cases.
   */
  function show(): void {
    if (container.hasAttribute('open')) return;
    if (typeof container.showModal === 'function') {
      container.showModal();
    } else {
      container.setAttribute('open', '');
    }
  }

  function position(anchor: DOMRect): void {
    // Render invisibly first so the real height can be measured.
    container.style.visibility = 'hidden';
    show();
    const { width, height } = container.getBoundingClientRect();

    let left = anchor.left;
    left = Math.min(left, window.innerWidth - width - VIEWPORT_MARGIN);
    left = Math.max(left, VIEWPORT_MARGIN);

    let top = anchor.bottom + 8;
    if (top + height > window.innerHeight - VIEWPORT_MARGIN) {
      top = anchor.top - height - 8;
    }
    top = Math.max(top, VIEWPORT_MARGIN);

    container.style.left = `${left}px`;
    container.style.top = `${top}px`;
    container.style.visibility = 'visible';
  }

  return {
    open(anchor: DOMRect, selector: string, prefillEmail?: string) {
      previousFocus = document.activeElement;
      selectorPreview.textContent = selector;
      textarea.value = '';
      textarea.disabled = false;
      if (emailInput) {
        emailInput.value = prefillEmail ?? '';
        emailInput.disabled = false;
      }
      submitButton.disabled = false;
      submitButton.textContent = messages.submit;
      setStatus(null);
      lastAnchor = anchor;
      setPreviewing(false);
      setFullscreen(false);
      const stored = loadDialogSize(safeLocalStorage(), {
        width: window.innerWidth,
        height: window.innerHeight,
      });
      if (stored) {
        container.style.width = `${stored.width}px`;
        container.style.height = `${stored.height}px`;
      }
      position(anchor);
      openedSize = { width: container.offsetWidth, height: container.offsetHeight };
      textarea.focus();
    },
    close() {
      if (!fullscreen && container.hasAttribute('open')) {
        const { offsetWidth, offsetHeight } = container;
        const resized =
          offsetWidth > 0 &&
          offsetHeight > 0 &&
          (!openedSize || offsetWidth !== openedSize.width || offsetHeight !== openedSize.height);
        if (resized) {
          saveDialogSize(
            safeLocalStorage(),
            clampSize(
              { width: offsetWidth, height: offsetHeight },
              { width: window.innerWidth, height: window.innerHeight },
            ),
          );
        }
      }
      if (container.open && typeof container.close === 'function') {
        container.close();
      } else {
        container.removeAttribute('open');
      }
      if (previousFocus instanceof HTMLElement) {
        previousFocus.focus({ preventScroll: true });
      }
      previousFocus = null;
    },
    escape: handleEscape,
    setBusy(busy: boolean) {
      textarea.disabled = busy;
      if (emailInput) emailInput.disabled = busy;
      submitButton.disabled = busy;
      cancelButton.disabled = busy;
      setStatus(busy ? 'sending' : null);
    },
    showSuccess() {
      cancelButton.disabled = false;
      setStatus('success');
    },
    showError() {
      textarea.disabled = false;
      if (emailInput) emailInput.disabled = false;
      submitButton.disabled = false;
      cancelButton.disabled = false;
      submitButton.textContent = messages.retry;
      setStatus('error');
    },
    destroy() {
      container.remove();
    },
  };
}
